/**
 * Pure tree logic for conversation message display.
 * Builds a tree index from a flat messages array and computes a single
 * linear "active path" through the tree based on branch selections.
 */

export interface TreeMessage {
  id: string;
  content: string;
  is_user: boolean;
  created_at: string;
  kind?: "CHAT" | "ARTF" | "QUESTION" | "ANSWER" | "EDIT";
  model?: string;
  tokens?: number;
  is_edited?: boolean;
  is_regenerated?: boolean;
  retrieval_latency_ms?: number;
  model_latency_ms?: number;
  retrieval_hit_count?: number;
  parent_message_id?: string;
  client_message_id?: string | null;
  version?: number;
  thinking_method_id?: string | null;
}

export interface TreeIndex {
  /** All messages keyed by id */
  byId: Map<string, TreeMessage>;
  /** Parent id -> array of children, sorted by version then created_at */
  childrenOf: Map<string, TreeMessage[]>;
  /** Messages with no parent */
  roots: TreeMessage[];
}

export interface VersionInfo {
  siblings: TreeMessage[];
  currentIndex: number;
  total: number;
}

/**
 * Build an index of the message tree from a flat array.
 */
export function buildTreeIndex(messages: TreeMessage[]): TreeIndex {
  const byId = new Map<string, TreeMessage>();
  const childrenOf = new Map<string, TreeMessage[]>();
  const roots: TreeMessage[] = [];

  for (const m of messages) {
    byId.set(m.id, m);
  }

  for (const m of messages) {
    if (m.parent_message_id && byId.has(m.parent_message_id)) {
      let children = childrenOf.get(m.parent_message_id);
      if (!children) {
        children = [];
        childrenOf.set(m.parent_message_id, children);
      }
      children.push(m);
    } else {
      roots.push(m);
    }
  }

  // Sort children: by version (ascending), then by created_at (ascending)
  for (const children of childrenOf.values()) {
    children.sort((a, b) => {
      const va = a.version ?? 0;
      const vb = b.version ?? 0;
      if (va !== vb) return va - vb;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  }

  // Sort roots by created_at
  roots.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  return { byId, childrenOf, roots };
}

/**
 * Determine whether a child message is a "user edit" of its parent.
 * User edits are user CHAT messages whose parent is also a user CHAT message.
 */
function isUserEdit(child: TreeMessage, parent: TreeMessage): boolean {
  return child.is_user && parent.is_user && child.kind === "CHAT" && parent.kind === "CHAT";
}

/**
 * Find the root of an edit chain: walk up through user-edit parents
 * until we reach a message whose parent is NOT a user CHAT (i.e. the first
 * user message in the chain).
 */
function findEditChainRoot(index: TreeIndex, message: TreeMessage): TreeMessage {
  let current = message;
  while (current.parent_message_id) {
    const parent = index.byId.get(current.parent_message_id);
    if (parent && isUserEdit(current, parent)) {
      current = parent;
    } else {
      break;
    }
  }
  return current;
}

/**
 * Collect all versions in an edit chain starting from the chain root.
 * Walks down through user-edit children depth-first, collecting every node.
 * Returns [root, edit1, edit2, ...] in tree order.
 */
function collectEditChainVersions(index: TreeIndex, chainRoot: TreeMessage): TreeMessage[] {
  const versions: TreeMessage[] = [chainRoot];
  const stack = [chainRoot];
  while (stack.length > 0) {
    const node = stack.pop()!;
    const children = index.childrenOf.get(node.id) || [];
    const edits = children.filter(c => isUserEdit(c, node));
    // Sort edits so we process them in order (version then created_at)
    for (const edit of edits) {
      versions.push(edit);
      stack.push(edit);
    }
  }
  return versions;
}

/**
 * Get the non-edit children of a message (assistant responses, artifacts, etc.)
 */
function getNormalChildren(index: TreeIndex, message: TreeMessage): TreeMessage[] {
  const children = index.childrenOf.get(message.id) || [];
  return children.filter(c => !isUserEdit(c, message));
}

/**
 * Compute the linear active path through the conversation tree.
 *
 * At each node, we look at its children and decide which branch to follow:
 * - If a user message has user-edit children, we pick the selected edit
 *   (or latest by default) and display it in place of the original,
 *   then follow that edit's subtree.
 * - If a message has multiple assistant children (regenerations),
 *   we pick the selected one (or latest by default).
 * - Otherwise, we follow the single child.
 *
 * selectedBranches: Record<parentMessageId, selectedChildId>
 */
export function computeActivePath(
  index: TreeIndex,
  selectedBranches: Record<string, string>
): TreeMessage[] {
  const path: TreeMessage[] = [];

  // Pick the root: use selection or first root
  if (index.roots.length === 0) return path;
  const selectedRootId = selectedBranches["__root__"];
  let current: TreeMessage | undefined = selectedRootId
    ? index.roots.find(r => r.id === selectedRootId) ?? index.roots[0]
    : index.roots[0];

  while (current) {
    const children = index.childrenOf.get(current.id) || [];

    // Check if this user message has any edits (direct or chained)
    const userEdits = children.filter(c => isUserEdit(c, current!));

    if (userEdits.length > 0) {
      // This user message has edits: flatten the entire edit chain.
      // Collect ALL versions: [current, edit1, edit1's edit, ...]
      const allVersions = collectEditChainVersions(index, current);

      // Find which version is selected (check selection at chain root)
      const selectedId = selectedBranches[current.id];
      let selectedVersion: TreeMessage;

      if (selectedId) {
        selectedVersion = allVersions.find(v => v.id === selectedId) ?? allVersions[allVersions.length - 1];
      } else {
        // Default: latest version in the chain
        selectedVersion = allVersions[allVersions.length - 1];
      }

      // Push the selected version (only one!) to the path
      path.push(selectedVersion);

      // Follow the selected version's NON-edit children
      const normalCh = getNormalChildren(index, selectedVersion);
      current = pickFromCandidates(normalCh, selectedBranches[selectedVersion.id]);
    } else if (children.length === 0) {
      path.push(current);
      break;
    } else {
      // No user edits: normal flow
      path.push(current);
      current = pickFromCandidates(children, selectedBranches[current.id]);
    }
  }

  return path;
}

/**
 * Pick the next child to follow from a list of candidate children.
 */
function pickFromCandidates(
  candidates: TreeMessage[],
  selectedId?: string
): TreeMessage | undefined {
  if (candidates.length === 0) return undefined;
  if (selectedId) {
    const selected = candidates.find(c => c.id === selectedId);
    if (selected) return selected;
  }
  // Default: latest by created_at (candidates are already sorted)
  return candidates[candidates.length - 1];
}

/**
 * Get the default branch selections: at each fork, select the latest child.
 */
export function getDefaultBranchSelections(index: TreeIndex): Record<string, string> {
  const selections: Record<string, string> = {};
  const visitedChainRoots = new Set<string>();

  for (const [parentId, children] of index.childrenOf.entries()) {
    const parent = index.byId.get(parentId);
    if (!parent) continue;

    // For user messages with edits, flatten the entire chain and default to latest
    const userEdits = children.filter(c => isUserEdit(c, parent));
    if (userEdits.length > 0) {
      // Find chain root (might be parent itself or higher)
      const chainRoot = findEditChainRoot(index, parent);
      if (!visitedChainRoots.has(chainRoot.id)) {
        visitedChainRoots.add(chainRoot.id);
        const allVersions = collectEditChainVersions(index, chainRoot);
        // Default: select last version in the chain, keyed by chain root
        selections[chainRoot.id] = allVersions[allVersions.length - 1].id;
      }
      continue;
    }

    // For messages with multiple children of same role, default to latest
    if (children.length > 1) {
      selections[parentId] = children[children.length - 1].id;
    }
  }

  return selections;
}

/**
 * Get version info for a message that's currently in the active path.
 * For user edits, walks the full edit chain to collect all versions.
 */
export function getVersionInfoForPathMessage(
  index: TreeIndex,
  message: TreeMessage
): VersionInfo {
  const noVersions: VersionInfo = { siblings: [message], currentIndex: 0, total: 1 };

  // User CHAT message: check if it's part of an edit chain
  if (message.is_user && message.kind === "CHAT") {
    const chainRoot = findEditChainRoot(index, message);
    const allVersions = collectEditChainVersions(index, chainRoot);
    if (allVersions.length <= 1) return noVersions;
    const idx = allVersions.findIndex(v => v.id === message.id);
    return { siblings: allVersions, currentIndex: Math.max(idx, 0), total: allVersions.length };
  }

  // Assistant message with siblings
  if (!message.is_user && message.parent_message_id) {
    const allSiblings = (index.childrenOf.get(message.parent_message_id) || [])
      .filter(c => !c.is_user);
    if (allSiblings.length <= 1) return noVersions;
    const idx = allSiblings.findIndex(s => s.id === message.id);
    return { siblings: allSiblings, currentIndex: Math.max(idx, 0), total: allSiblings.length };
  }

  return noVersions;
}
